import React, { useEffect, useRef, useState } from "react";
import locale from "date-fns/locale/en-US";
import { withSnackbar } from "notistack";
import { connect } from "react-redux";
import DateFnsUtils from "@date-io/date-fns";
import { Button, Grid, Tooltip, Typography, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import imageProjectIllustration from "../../../../assets/images/projects-illustration.png";
import {
  setProjects,
  setProjectsQuantity
} from "../../../../store/actions/account";
import {
  setOptionsExpanded,
  toggleDrawer
} from "../../../../store/actions/common";
import {
  resetProject,
  setCurrentWindow
} from "../../../../store/actions/project";
import API from "../../../../utils/axios";
import {
  compare,
  getDateValue,
  isEmptyObj,
  remCalc,
  checkIsFOJW
} from "../../../../utils/helpers";
import history from "../../../../utils/history";
import CommonMaterialTable from "../../../accountDrawer/commonTable/CommonMaterialTable";
import mixpanelWrapper from "../../../../utils/mixpanel";
import { TAB_IDS } from "../../../data/constants";
import clsx from "clsx";
import DropdownActionsMenu from "./DropdownActionsMenu";
import ConfirmPasswordDialog from "../../../dialogs/ConfirmPasswordDialog";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../../../states/tour/tours/product";
import TitleWithEducationalMaterials from "../../../common/TitleWithEducationalMaterials";

const dateFns = new DateFnsUtils();

if (locale && locale.options) {
  locale.options.weekStartsOn = 1;
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    margin: theme.spacing(4, 0),
    borderStyle: "solid",
    borderColor: "#DBDCE1",
    borderWidth: 0
  },
  wrapperLoaded: {
    borderWidth: 1
  },
  noProjectWrapper: {
    padding: theme.spacing(4)
  },
  noProjectTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    margin: theme.spacing(1, 0)
  },
  noProjectDescription: {
    color: "#808080"
  },
  addButton: {
    textTransform: "none",
    marginLeft: theme.spacing(1.5),
    boxShadow: "none",
    fontWeight: "normal",
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    "& svg": {
      color: "#fff",
      fontSize: "24px !important"
    },
    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "#1329FE",
      color: "#fff",
      opacity: "38%"
    }
  },
  tooltip: {
    backgroundColor: "#fff",
    color: theme.palette.text.primary,
    fontSize: "0.8125rem",
    lineHeight: "1.1875rem",
    fontWeight: 400,
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.24)",
    padding: theme.spacing(1.5),
    maxWidth: "216px"
  },
  customTableWrapper: {
    "& .MuiPaper-root div div .MuiTable-root": {
      width: "100%"
    }
  }
}));

const tabs = [
  {
    label: "Upcoming",
    id: TAB_IDS.UPCOMING
  },
  {
    label: "Active",
    id: TAB_IDS.ACTIVE
  },
  {
    label: "Past",
    id: TAB_IDS.PAST
  }
];

const statusByTab = {
  [TAB_IDS.UPCOMING]:
    "created,preparing,optimized,reoptimized,published,processing,error",
  [TAB_IDS.ACTIVE]: "in_progress",
  [TAB_IDS.PAST]: "finished,expired"
};

const ProjectsOverview = props => {
  const {
    enqueueSnackbar,
    projects,
    setProjects,
    setProjectsQuantity,
    setCurrentWindow,
    resetProject,
    stopsLength,
    vehiclesLength,
    status,
    totalQuantity,
    published_once,
    drawerOpen,
    toggleDrawer,
    duplicateId,
    duplicateSaved,
    setDuplicateId,
    setDuplicateSaved,
    projectsRef,
    setCurrentProject,
    setProjectsRef,
    hasAnyProject,
    setHasAnyProject,
    currentTabId,
    setCurrentTabId,
    setOptionsExpanded,
    isEditingDuplicate,
    setCurrentAction,
    expired,
    incompleteAccount,
    incompletePayment,
    failedToProcess,
    setAccountDrawerOpen,
    reloadCurrentPage,
    setReloadCurrentPage
  } = props;

  // General
  const classes = useStyles();

  // States
  const [confirmDialogState, setConfirmDialogState] = useState({
    open: false,
    id: null
  });

  const [selected, setSelected] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [projectsFirstPageLoaded, setProjectsFirstPageLoaded] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [loadCompleted, setLoadComplete] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const passwordActionRef = useRef(null);

  // Variables
  const activeProject = sessionStorage.currentProject;
  const isFinishedProject = status => ["finished", "expired"].includes(status);
  /*
    TODO: As the projects page is now the default route in case of no route, the userActive, to disable any projects
     related actions.
   */

  const userActive =
    !expired && !incompleteAccount && !incompletePayment && !failedToProcess;

  const dropdownActionsMenuRef = useRef(null);
  const productTour = useProductTour();

  const promptPassword = action => {
    passwordActionRef.current = action;
    setPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async () => {
    const action = passwordActionRef.current;
    passwordActionRef.current = null;
    if (action) {
      await action();
    }
  };

  const handlePasswordClose = () => {
    passwordActionRef.current = null;
    setPasswordDialogOpen(false);
  };

  // Callbacks
  const checkHasAnyProjects = () => {
    API.get(`/v1/projects?page=0`)
      .then(res => {
        if (res.data.total_quantity > 0) {
          setHasAnyProject(true);
        } else {
          setHasAnyProject(false);
        }
        setProjectsFirstPageLoaded(true);
      })
      .catch(() => {
        enqueueSnackbar(`Something went wrong, couldn't load the projects.`);
      });
  };

  const getProjectsPerPage = newTab => {
    setLoadingProjects(true);
    setProjectsRef([]);
    API.get(
      `/v1/projects?status=${statusByTab[newTab || currentTabId]
      }&page=${currentPage}`
    )
      .then(res => {
        setProjects(res.data.projects);
        setProjectsQuantity(res.data.total_quantity);
        mixpanelWrapper.people.set({
          Projects: res.data.total_quantity
        });

        setLoadComplete(true);
        const wrapper = document.querySelector("#tableWrapper > .MuiPaper-root")
          ?.childNodes[1];
        if (wrapper) {
          wrapper.scrollTop = 0;
        }
      })
      .catch(err => console.log(err))
      .finally(() => {
        setLoadingProjects(false);
      });
  };

  const handleMultipleDelete = options => {
    setConfirmDialogState({
      open: true,
      title: options.isInProgress
        ? "Are you sure?"
        : `Delete project${selected.length > 1 ? "s" : ""}?`,
      body: options.isInProgress
        ? "You have selected one or more projects that are In Progress. Do you want to delete the selected projects?"
        : `This will permanently delete ${selected.length} project${selected.length > 1 ? "s" : ""}. You will not be able to recover the data once deleted.`,
      action: () => {
        deleteProjects(selected);
        setSelected([]);
      }
    });
  };

  const deleteProjects = selectedProjects => {
    const oldProjects = [...projects];
    setSelected([]);

    const execute = () => {
      setProjectsRef([]);
      setLoadingProjects(true);

      if (selectedProjects.includes(activeProject)) {
        sessionStorage.removeItem("currentProject");
        history.push("/projects", {
          action: "delete"
        });
        resetProject();
      }

      let project;
      if (selectedProjects.length === 1) {
        project = oldProjects.find(item => item.id === selectedProjects[0]);
      }

      API.delete(`/v1/projects/batch`, { data: [...selectedProjects] })
        .then(() => {
          enqueueSnackbar(
            !project
              ? `Selected projects deleted.`
              : `Project "${project?.name}" deleted.`
          );

          if (projects.length === selectedProjects.length && currentPage !== 0) {
            setCurrentPage(currentPage - 1);
          } else {
            checkHasAnyProjects();
            getProjectsPerPage();
          }
        })
        .catch(err => {
          setLoadingProjects(false);

          if (err && err.status === 400) {
            enqueueSnackbar(err.data.message);
          } else {
            enqueueSnackbar(`Something went wrong, couldn't delete the project.`);
          }
          setProjects([...oldProjects]);
        });
    };

    if (checkIsFOJW()) {
      promptPassword(execute);
      return;
    }

    execute();
  };

  const handleDelete = project => {
    setConfirmDialogState({
      open: true,
      id: project.id,
      title: `Delete project?`,
      body: `This will permanently delete 1 project. You will not be able to recover the data once deleted.`,
      action: () => deleteProject(project)
    });
  };

  const deleteProject = project => {
    const execute = () => {
      setProjectsRef([]);
      setLoadingProjects(true);
      if (project.id === activeProject) {
        sessionStorage.removeItem("currentProject");
        history.push("/projects", {
          action: "delete"
        });
        resetProject();
      }
      setSelected(prevSelected => prevSelected.filter(id => id !== project.id));
      API.delete(`/v1/projects/${project.id}`)
        .then(() => {
          enqueueSnackbar(`Project "${project.name}" deleted.`);

          if (projects.length === 1 && currentPage !== 0) {
            setCurrentPage(currentPage - 1);
          } else {
            checkHasAnyProjects();
            getProjectsPerPage();
          }
        })
        .catch(err => {
          setLoadingProjects(false);
          if (err && err.status === 400) {
            enqueueSnackbar(err.data.message);
          } else {
            enqueueSnackbar(`Something went wrong, couldn't delete the project.`);
          }
          setProjects([...projects]);
        });
    };

    if (checkIsFOJW()) {
      promptPassword(execute);
      return;
    }

    execute();
  };

  const openProject = (e, rowData) => {
    // set current project
    if (rowData.id === sessionStorage.currentProject) {
      history.push(rowData.id);
      drawerOpen === false && toggleDrawer(true);
    } else {
      resetProject();
      sessionStorage.currentProject = rowData.id;
      history.push(rowData.id);
      setCurrentWindow("Project");
      setOptionsExpanded(false);
    }
  };

  const handleDuplicate = rowData => {
    setCurrentProject(rowData);
    setCurrentAction("duplicate");
  };

  const handleAdd = () => {
    if (
      productTour.isTourStepId(
        PRODUCT_TOUR_STEP_IDS.PART_INITIALIZE_NEW_PROJECT_STEP_NEW_PROJECT_BUTTON
      )
    ) {
      productTour.goToNext();
    }
    setCurrentProject(null);
    setCreatingProject(true);
    API.post("/v1/projects", {
      name: "Untitled project",
      date: dateFns.format(new Date(), "dd/MM/yyyy"),
      enable_vehicles: true
    })
      .then(response => {
        //setMenuOpen(false);
        window.mixpanel && window.mixpanel.track("Create project");
        enqueueSnackbar(`Project "${response.data.name}" created.`);
        history.push(`/${response.data.id}?focusTitle`);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't create a project.`);
      })
      .finally(() => {
        setCreatingProject(false);
      });
    //setCurrentAction("add");
  };

  const handleEdit = rowData => {
    setCurrentProject(rowData);
    setCurrentAction("edit");
  };

  // Effects
  useEffect(() => {
    const newProjects = projects.map(project => {
      const project_date = getDateValue(project.project_date);
      const published_once_project =
        project.id === activeProject ? published_once : project.published_once;

      const rowStatus =
        project.id === activeProject
          ? status && status.toLowerCase()
          : project.status && project.status.toLowerCase();

      const in_progress_project = project.project_started;

      let currentStatus = "";

      // show status as one of Open, Planning, Published, Completed, Expired
      if (in_progress_project && !isFinishedProject(rowStatus)) {
        currentStatus = "In Progress";
      } else if (published_once_project && !isFinishedProject(rowStatus)) {
        currentStatus = "Published";
      } else {
        switch (rowStatus) {
          case "created":
          case "to do":
          case "preparing":
          case undefined:
          case null:
          case false:
            currentStatus = "Open";
            break;
          case "optimized":
          case "reoptimized":
            currentStatus = "Planning";
            break;
          case "in_progress":
            currentStatus = "In Progress";
            break;
          case "published":
            currentStatus = "Published";
            break;
          case "finished":
            currentStatus = "Completed";
            break;
          default:
            // Capitalize
            currentStatus = rowStatus
              ? rowStatus.charAt(0).toUpperCase() +
              rowStatus.slice(1).toLowerCase()
              : "";
        }
      }

      return {
        ...project,
        project_date: project_date
          ? dateFns.format(new Date(project_date), "dd/MM/yyyy")
          : "",
        stops: project.id === activeProject ? stopsLength : project.stops || 0,
        vehicles:
          project.id === activeProject ? vehiclesLength : project.vehicles || 0,
        status: currentStatus
      };
    });
    setProjectsRef(newProjects);
    // projectsFirstPageLoaded && setHasAnyProject(Boolean(newProjects.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projects,
    status,
    published_once,
    stopsLength,
    vehiclesLength,
    activeProject
  ]);

  useEffect(() => {
    if (isEditingDuplicate.current) {
      setDuplicateId(isEditingDuplicate.current);
    }
    if (duplicateId && duplicateSaved) {
      const row = document.getElementById(`row-${duplicateId}`);

      if (row) {
        const index = row.getAttribute("index");
        const wrapper = document.querySelector("#tableWrapper > .MuiPaper-root")
          ?.childNodes[1];
        if (wrapper) {
          wrapper.scrollTop = (index || 0) * 48;
        }
      }
      setDuplicateId(null);
      setDuplicateSaved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingDuplicate, duplicateId, duplicateSaved, projectsRef]);

  useEffect(() => {
    checkHasAnyProjects();
  }, []);

  useEffect(() => {
    setProjectsRef([]);
    setProjectsQuantity(0);
  }, []);

  useEffect(() => {
    if (reloadCurrentPage) {
      getProjectsPerPage();
      setReloadCurrentPage(false);
    }
  }, [reloadCurrentPage]);

  return (
    <Paper
      className={clsx(
        classes.wrapper,
        projectsFirstPageLoaded && classes.wrapperLoaded
      )}
      elevation={0}
    >
      <div
        id="projects_overview"
        className={clsx(
          ((hasAnyProject && projectsFirstPageLoaded && loadCompleted) ||
            (!hasAnyProject && projectsFirstPageLoaded)) &&
          "projects-ready"
        )}
      >
        {projectsFirstPageLoaded && !hasAnyProject && (
          <Grid
            container
            direction="column"
            alignItems="center"
            justify="center"
            alignContent="center"
            spacing={3}
            className={classes.noProjectWrapper}
          >
            <Grid item>
              <img
                src={imageProjectIllustration}
                height={158}
                width={160}
                alt="Project illustration"
              />
            </Grid>
            <Grid item>
              <Typography className={classes.noProjectTitle} align="center">
                Ready. Set. Route.
              </Typography>
              <Typography
                align="center"
                className={classes.noProjectDescription}
              >
                When you create your first project, it will show up here.
                <br />
                Ready to plan your routes in seconds?
              </Typography>
            </Grid>
            <Grid item>
              <Button
                id="add_new_project_button"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                className={classes.addButton}
                onClick={() => {
                  handleAdd();
                }}
                disabled={!userActive}
              >
                New Project
              </Button>
            </Grid>
          </Grid>
        )}
        {projectsFirstPageLoaded && hasAnyProject && (
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
            <CommonMaterialTable
              key={projectsFirstPageLoaded ? currentTabId : "loading"}
              addButtonDisabled={!userActive || creatingProject}
              rowActionsDisabled={
                !userActive || productTour.hasActiveTourCurrently()
              }
              onRowClickDisabled={!userActive}
              inline={true}
              tabs={tabs}
              currentTabId={currentTabId}
              onTabChange={tabId => {
                setSelected([]);
                setCurrentPage(0);
                setCurrentTabId(tabId);
              }}
              confirmDialogState={confirmDialogState}
              setConfirmDialogState={setConfirmDialogState}
              selected={selected}
              customWrapperClass={classes.customTableWrapper}
              data={projectsRef}
              pagination={{
                enabled: true,
                totalQuantity,
                currentPage,
                setCurrentPage
              }}
              title={
                <TitleWithEducationalMaterials
                  title={"Projects"}
                  configId={"projects"}
                  sectionName={"projects"}
                  showVideo
                  showArticle
                  stickToRight
                />
              }
              disabled={!userActive}
              singular="project"
              plural="projects"
              columns={[
                {
                  title: "Name",
                  field: "name",
                  cellStyle: {
                    width: remCalc(200)
                  },
                  headerStyle: {
                    width: remCalc(200)
                  },
                  customSort: (a, b) => {
                    return compare(a.name, b.name);
                  },
                  render: rowData => (
                    <Tooltip
                      title={rowData.name}
                      placement="bottom-start"
                      classes={{ tooltip: classes.tooltip }}
                      enterDelay={1000}
                    >
                      <Typography noWrap data-cy="projects-rowName">
                        {rowData.name}
                      </Typography>
                    </Tooltip>
                  )
                },
                {
                  title: "Date",
                  field: "project_date",
                  defaultSort: "desc",
                  searchable: false,
                  cellStyle: {
                    width: remCalc(84)
                  },
                  headerStyle: {
                    width: remCalc(84)
                  },
                  render: rowData => {
                    let date;
                    try {
                      date = dateFns.format(
                        new Date(
                          getDateValue(
                            rowData.project_date.split("/").reverse().join("-")
                          )
                        ),
                        "dd MMM yyyy"
                      );
                    } catch (e) {
                      date = "";
                    }
                    return <span data-cy="projects-rowDate">{date}</span>;
                  },
                  customSort: (a, b) => {
                    // date sort
                    const dateA = a.project_date.split("/").reverse().join("-");
                    const dateB = b.project_date.split("/").reverse().join("-");
                    if (+new Date(dateA) !== +new Date(dateB)) {
                      return new Date(dateA) - new Date(dateB);
                    }
                    return new Date(+a.created_at) - new Date(+b.created_at);
                  }
                },
                {
                  title: "Stops",
                  field: "stops",
                  editable: "never",
                  cellStyle: {
                    textAlign: "right"
                  },
                  headerStyle: {
                    textAlign: "right"
                  },
                  type: "numeric",
                  initialEditValue: "0",
                  searchable: false,
                  customSort: (a, b) => +a.stops - +b.stops,
                  render: rowData => {
                    return (
                      <span data-cy="projects-rowStops">{rowData.stops}</span>
                    );
                  }
                },
                {
                  title: "Vehicles",
                  field: "vehicles",
                  editable: "never",
                  cellStyle: {
                    textAlign: "right"
                  },
                  headerStyle: {
                    textAlign: "right"
                  },
                  type: "numeric",
                  initialEditValue: "0",
                  searchable: false,
                  customSort: (a, b) => +a.vehicles - +b.vehicles,
                  render: rowData => {
                    return (
                      <span data-cy="projects-rowVehicles">
                        {rowData.vehicles}
                      </span>
                    );
                  }
                },
                {
                  title: "Status",
                  field: "status",
                  editable: "never",
                  initialEditValue: "Open",
                  searchable: false,
                  render: rowData => {
                    return (
                      <span data-cy="projects-rowStatus">{rowData.status}</span>
                    );
                  }
                }
              ]}
              handleMultipleDelete={handleMultipleDelete}
              getDataPerPage={
                projectsFirstPageLoaded ? getProjectsPerPage : () => { }
              }
              setSelected={setSelected}
              activeItem={activeProject}
              loading={loadingProjects || !projectsFirstPageLoaded}
              handleAdd={handleAdd}
              addDisabled={creatingProject}
              handleEdit={handleEdit}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              onRowClick={openProject}
              duplicateAllowed
              dropdownActions={true}
              openDropdownActionsMenu={
                dropdownActionsMenuRef.current &&
                dropdownActionsMenuRef.current.open
              }
              addId="add_new_project_button"
            />
            <DropdownActionsMenu
              ref={dropdownActionsMenuRef}
              onEditClick={handleEdit}
              onDuplicateClick={handleDuplicate}
              onDeleteClick={handleDelete}
              addId="add_new_project_button"
            />
            <ConfirmPasswordDialog
              open={passwordDialogOpen}
              handleClose={handlePasswordClose}
              onConfirm={handlePasswordConfirm}
            />
          </MuiPickersUtilsProvider>
        )}
      </div>
    </Paper>
  );
};

const mapStateToProps = state => {
  const project = state.account.projects.find(
    project => project.id === sessionStorage.currentProject
  );
  const stopsLength = state.project.vrp.stops.filter(
    task =>
      task.type !== "start" &&
      task.type !== "end" &&
      task.type !== "break" &&
      !isEmptyObj(task)
  ).length;
  const vehiclesLength = state.project.selectedVehicles.length;
  const { status } = state.project;
  const { published_once } = state.project;

  return {
    projects: state.account.projects,
    totalQuantity: state.account.totalQuantity,
    stopsLength: state.project.id || !project ? stopsLength : +project.stops,
    drawerOpen: state.common.drawerOpen,
    vehiclesLength:
      state.project.id || !project ? vehiclesLength : +project.vehicles,
    status:
      state.project.id || !project
        ? status && status.toLowerCase()
        : project.status.toLowerCase(),

    published_once:
      state.project.id || !project ? published_once : project.published_once
  };
};

export default connect(mapStateToProps, {
  setProjects,
  setProjectsQuantity,
  setCurrentWindow,
  resetProject,
  toggleDrawer,
  setOptionsExpanded
})(withSnackbar(ProjectsOverview));
